import { useState, useEffect } from 'react';
import { URLMediaCache } from '../services/cache/URLMediaCache';
import { logger } from '../utils/logger';

interface UseMediaCacheOptions {
  /** Whether to automatically preload and cache the media */
  autoCache?: boolean;
  /** Timeout for cache operations in milliseconds */
  cacheTimeout?: number;
}

interface MediaCacheResult {
  /** The media URL to use (same as input for URL-based caching) */
  mediaUrl: string | undefined;
  /** Whether the cache operation is in progress */
  isLoading: boolean;
  /** Any error that occurred during caching */
  error: string | null;
  /** Whether this media is cached (metadata level) */
  isCached: boolean;
}

/**
 * Hook for URL-based image caching that reduces R2 costs
 * Uses browser's native HTTP cache + our TTL management
 */
export function useCachedImage(
  imageUrl: string | undefined, 
  options: UseMediaCacheOptions = {}
): MediaCacheResult {
  const { autoCache = true, cacheTimeout = 5000 } = options;
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCached, setIsCached] = useState(false);

  useEffect(() => {
    if (!imageUrl || !autoCache) {
      setIsCached(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    // Check if already cached (metadata level)
    const cacheKey = `media_cache_${btoa(imageUrl.split('?')[0]).slice(0, 50)}`;
    const cachedData = localStorage.getItem(cacheKey);
    
    if (cachedData) {
      try {
        const parsed = JSON.parse(cachedData);
        const isExpired = Date.now() - parsed.timestamp > (30 * 24 * 60 * 60 * 1000);
        if (!isExpired && parsed.type === 'image') {
          setIsCached(true);
          setIsLoading(false);
          return;
        }
      } catch {
        // Invalid cache data, continue with loading
      }
    }

    // Preload image to trigger browser caching
    const img = new Image();
    const timeoutId = setTimeout(() => {
      setError('Cache timeout');
      setIsLoading(false);
    }, cacheTimeout);

    img.onload = () => {
      clearTimeout(timeoutId);
      // Cache the URL metadata (browser already cached the actual image)
      URLMediaCache.cacheMedia(imageUrl, 'image');
      setIsCached(true);
      setIsLoading(false);
    };

    img.onerror = () => {
      clearTimeout(timeoutId);
      setError('Failed to load image');
      setIsLoading(false);
    };

    img.src = imageUrl;

    return () => {
      clearTimeout(timeoutId);
    };
  }, [imageUrl, autoCache, cacheTimeout]);

  return {
    mediaUrl: imageUrl, // Always return original URL (browser handles caching)
    isLoading,
    error,
    isCached
  };
}

/**
 * Hook for URL-based video caching that reduces R2 costs
 */
export function useCachedVideo(
  videoUrl: string | undefined,
  options: UseMediaCacheOptions = {}
): MediaCacheResult {
  const { autoCache = true, cacheTimeout = 10000 } = options;
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCached, setIsCached] = useState(false);

  useEffect(() => {
    if (!videoUrl || !autoCache) {
      setIsCached(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    // Check if already cached (metadata level)  
    const cacheKey = `media_cache_${btoa(videoUrl.split('?')[0]).slice(0, 50)}`;
    const cachedData = localStorage.getItem(cacheKey);
    
    if (cachedData) {
      try {
        const parsed = JSON.parse(cachedData);
        const isExpired = Date.now() - parsed.timestamp > (30 * 24 * 60 * 60 * 1000);
        if (!isExpired && parsed.type === 'video') {
          setIsCached(true);
          setIsLoading(false);
          return;
        }
      } catch {
        // Invalid cache data, continue with loading
      }
    }

    // Create video element to trigger browser caching
    const video = document.createElement('video');
    const timeoutId = setTimeout(() => {
      setError('Cache timeout');
      setIsLoading(false);
    }, cacheTimeout);

    const handleCanPlay = () => {
      clearTimeout(timeoutId);
      // Cache the URL metadata (browser already cached the actual video)
      URLMediaCache.cacheMedia(videoUrl, 'video');
      setIsCached(true);
      setIsLoading(false);
    };

    const handleError = () => {
      clearTimeout(timeoutId);
      setError('Failed to load video');
      setIsLoading(false);
    };

    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('error', handleError);
    
    // Preload metadata to trigger caching
    video.preload = 'metadata';
    video.src = videoUrl;

    return () => {
      clearTimeout(timeoutId);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('error', handleError);
    };
  }, [videoUrl, autoCache, cacheTimeout]);

  return {
    mediaUrl: videoUrl, // Always return original URL (browser handles caching)
    isLoading,
    error,
    isCached
  };
}

/**
 * Hook for preloading multiple media items
 */
export function usePreloadMedia(mediaUrls: Array<{ url: string; type: 'image' | 'video' }>) {
  const [preloadStatus, setPreloadStatus] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (mediaUrls.length === 0) return;

    const preloadPromises = mediaUrls.map(async ({ url, type }) => {
      try {
        if (type === 'image') {
          const img = new Image();
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
            img.src = url;
          });
          URLMediaCache.cacheMedia(url, 'image');
        } else {
          const video = document.createElement('video');
          await new Promise((resolve, reject) => {
            video.addEventListener('canplay', resolve);
            video.addEventListener('error', reject);
            video.preload = 'metadata';
            video.src = url;
          });
          URLMediaCache.cacheMedia(url, 'video');
        }
        
        setPreloadStatus(prev => ({ ...prev, [url]: true }));
      } catch (error) {
        logger.warn(`Failed to preload ${type}`, { url, error });
        setPreloadStatus(prev => ({ ...prev, [url]: false }));
      }
    });

    return () => {
      // Cleanup if component unmounts
    };
  }, [mediaUrls]);

  return preloadStatus;
}